import {
	Component, OnInit, OnChanges, SimpleChanges, ViewChild,
	Input, Output, ViewEncapsulation, EventEmitter } from '@angular/core';
import { language, notification, FormCollectorDirective } from 'curvy';
import { Task } from '@task-utils/types';
import { User } from '@task-modules/users/users';
import { users_translations } from '@task-modules/users/users.trans';
import UT = Task.USER_TYPE;
import { TaskRoutes } from '@task-utils/routes';
import { Replace } from '@task-utils/utils';
import { ShortLocation } from '@task-modules/tickets';

type TempRequestType = Replace<Pick<Task.TempStaffRequest, 'title' | 'date_from' | 'date_to' | 'number_of_workers' | 'shifts' | 'location_id' | 'description'>, 'date_from' | 'date_to', string>;

type TempRequestTweakType = Replace<TempRequestType, 'date_from' | 'date_to', Date|null>;

@Component({
	selector: 'task-create-temp-staff-request',
	encapsulation: ViewEncapsulation.None,
	template: `
		<div class="flex-column fullheight-noscroll">
			<h2 class="modal-title">
				{{ 'TEMP_STAFF_REQUEST.NEW' | trans }}
			</h2>

			<div style="overflow: auto;">
				<div class="field-grid" form-collector #tempStaffRequestForm="collector">
					<label class="input outlined" [title]="'TITLE' | trans">
						<input [(ngModel)]="request.title"
							conditions="nonempty" />
					</label>

					<!-- select label with options for is it a period or a single day -->
					<label class="input outlined" [title]="'TEMP_STAFF_REQUEST.DATE_TYPE' | trans">
						<select [(ngModel)]="date_type">
							<option value="period">{{ 'TEMP_STAFF_REQUEST.PERIOD' | trans }}</option>
							<option value="single_day">{{ 'TEMP_STAFF_REQUEST.SINGLE_DAY' | trans }}</option>
						</select>
				</label>

					@if (date_type == 'period') {
						<task-date-picker-input
							[(ngModel)]="request.date_from"
							[required]="true"
							[label]="'TEMP_STAFF_REQUEST.DATE_FROM' | trans"
							[classes]="'flex-dynamic outlined mx-0'"

						></task-date-picker-input>

						<task-date-picker-input
							[(ngModel)]="request.date_to"
							[required]="true"
							[label]="'TEMP_STAFF_REQUEST.DATE_TO' | trans"
							[classes]="'flex-dynamic outlined mx-0'"
						></task-date-picker-input>
					}
					@else {
						<task-date-picker-input
							[(ngModel)]="request.date_from"
							[required]="true"
							[label]="'DATE' | trans"
							[classes]="'flex-dynamic outlined mx-0'"
							(ngModelChange)="request.date_to = request.date_from"
						></task-date-picker-input>
				
					}

					<label class="input outlined" [title]="'TEMP_STAFF_REQUEST.NUMBER_OF_WORKERS' | trans">
						<input type="number" 
							[(ngModel)]="request.number_of_workers"
							min="1"
							conditions="nonempty" />
					</label>

					<knw-auto-search
						[required]="true"
						#locationPicker
						[function]="search_locations"
						[to-label]="format_location_name"
						label="{{'LOCATION' | trans }}"
						[(ngModel)]="picked_location"
						(ngModelChange)="request.location_id = picked_location.location_id"
						[popover-width]="'auto'"
						[classes]="'outlined mx-0'"
					></knw-auto-search>
					

					<label class="input outlined whole-row" [title]="'TEMP_STAFF_REQUEST.SHIFTS' | trans">
						<textarea [(ngModel)]="request.shifts" rows="3" conditions="nonempty"></textarea>
					</label>

					<label class="input outlined whole-row" [title]="'DESCRIPTION' | trans">
						<textarea [(ngModel)]="request.description" rows="3" conditions="nonempty"></textarea>
					</label>

				</div>
			</div>

			<div class="flex-row modal-footer">
				<button color="primary"
						(click)="cancel()">
					<i icon="clear"></i>
					<span>{{ 'CANCEL' | trans }}</span>
				</button>

				<button class="filled"
						color="secondary"
						(click)="done()"
						[disabled]="tempStaffRequestForm.containsErrors()">
					<i icon="check"></i>
					<span>{{ 'SAVE' | trans }}</span>
				</button>

			</div>
		</div>
	`,
	styles: [`
		task-create-temp-staff-request {
			max-width: 600px;
		}

		.span-cols-full {
			grid-column: 1 / -1;
		}

		.mx-0 {
			margin-left: 0px!important;
			margin-right: 0px!important;
		}

		task-create-temp-staff-request knw-search-picker {
			display: contents;
		}

		.error-message {
			color: red;
			padding: 1em;
		}
	`],

})
export class CreateTempStaffRequestComponent implements OnInit {
	@ViewChild('tempStaffRequestForm')
	tempStaffRequestForm: FormCollectorDirective;
	
	request: TempRequestTweakType = {
		title: "",
		date_from: null as Date | null,
		date_to: null as Date | null,
		number_of_workers: 1,
		shifts: "",
		location_id: null,
		description: "",
	};

	@Output('result')
	result: EventEmitter<Task.TempStaffRequest | TempRequestType> = new EventEmitter();

	
	is_sys_admin = false;

	date_type = 'period' as 'period' | 'single_day';
	picked_location: ShortLocation = null;


	async update_location() {
      let default_loc = User.currentUser.locations.length == 1 &&
      !await User.currentTypeIs(Task.USER_TYPE.ADMIN, Task.USER_TYPE.SYSTEM_ADMIN);

      if (default_loc) {
         this.request.location_id = User.currentUser.locations[0].location_id;
         let loc = await TaskRoutes.locations.api_location_get_single(this.request.location_id);
         this.picked_location = loc.data;
      }
   }

	ngOnInit() {
		this.init();
	}

	async init() {
		language.load_translations(users_translations);
		
		// this.update_details();
		this.update_location();
	}

	// ngOnChanges(changes: SimpleChanges) {
	// 	if (changes["request"]) {
	// 		this.request = {...changes["request"].currentValue};
	// 	}

	// 	this.update_details();
	// }

	async search_locations(search: string) {
		let res = await TaskRoutes.locations.api_location_search_visible(
			{ search: [search, Task.STATUS.DELETED], attributes: ["cin+ +name", "#!:status_id"] },
			{ page_no: 1, page_size: 50 }
		);
		return res.data;
  }

	format_location_name(loc: Task.Location) {
			if (!loc) { return ""; }
		return `${loc.cin ?? ''} ${loc.name}`;
	}

	// async update_details() {
	// 	this.is_sys_admin = await User.currentTypeIs(UT.SYSTEM_ADMIN);
	// }

	done() {
		if (this.date_type == 'single_day') {
			this.request.date_to = this.request.date_from;
		}

		let reqest_to_emit = {
			...this.request,
			date_from: this.request.date_from?.toISOString().split("T" )[0],
			date_to: this.request.date_to?.toISOString().split("T" )[0],
		}

		
		this.result.emit(reqest_to_emit);
	}

	cancel() {
		this.result.emit(null);
	}

	
}
